import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { candidatesApi } from '@/lib/api/client';
import toast from 'react-hot-toast';

export const candidateKeys = {
  all: ['candidates'] as const,
  runs: () => [...candidateKeys.all, 'runs'] as const,
  run: (id: string) => [...candidateKeys.all, 'run', id] as const,
  scoreboard: () => [...candidateKeys.all, 'scoreboard'] as const,
};

export function useCandidateRuns() {
  return useQuery({ queryKey: candidateKeys.runs(), queryFn: () => candidatesApi.listRuns() });
}

/**
 * One run in full. `staleTime` is deliberate: the detail response re-prices four legs per
 * candidate against the live quote ladder, so a refetch on every focus would hammer the quote
 * source for figures that move in cents.
 */
export function useCandidateRun(id: string | null) {
  return useQuery({
    queryKey: candidateKeys.run(id ?? ''),
    queryFn: () => candidatesApi.getRun(id!),
    enabled: id !== null,
    staleTime: 60_000,
  });
}

export function useCandidateScoreboard() {
  return useQuery({ queryKey: candidateKeys.scoreboard(), queryFn: () => candidatesApi.scoreboard() });
}

export function useUploadCandidateRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (report: unknown) => candidatesApi.uploadRun(report),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
      toast.success(
        `${result.replaced ? 'Replaced' : 'Stored'} run: ${result.candidates} candidates, ${result.disqualified} disqualified, ${result.linked} linked`
      );
    },
    // fetchApi rejects with the server's own `error` text on a 400, which is the rejection
    // reason the bot report schema produced -- far more useful than a generic failure.
    onError: (error: Error) => toast.error(error.message || 'Upload rejected'),
  });
}

/**
 * Both link mutations invalidate the journal as well as the candidates: the bot line on a
 * journal entry is served by `GET /journal`, so a link cleared here would keep rendering on a
 * cached feed otherwise.
 */
export function useUnlinkCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => candidatesApi.unlink(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
      queryClient.invalidateQueries({ queryKey: ['journal'] });
      toast.success('Link cleared');
    },
    onError: (error: Error) => toast.error(error.message || 'Could not clear the link'),
  });
}

export function useLinkAllCandidates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => candidatesApi.linkAll(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
      queryClient.invalidateQueries({ queryKey: ['journal'] });
      toast.success(`Linked ${result.linked}, relinked ${result.relinked}, lost ${result.lost}`);
    },
    onError: (error: Error) => toast.error(error.message || 'Match failed'),
  });
}
